import { useCallback } from "react";
import styled from "@emotion/styled";
import { useQueryClient } from "@tanstack/react-query";
import { Navigate } from "@tanstack/react-router";

import { DetailsContent } from "components/app_layout/DetailsLayout";
import { CollapseGroup } from "components/bid_module/common_report_components/ReportCollapse";
import { ReportNoticeDetails } from "components/bid_module/common_report_components/ReportNoticeDetails";
import toast from "components/ui/toast/Toast";
import { updateBidReportAnswersCache } from "lib/hooks/api/bid/answers/useGetBidReportAnswers";
import { useStreamBidReport } from "lib/hooks/api/bid/answers/useStreamBidReport";
import { useBidReport } from "lib/hooks/api/bid/bid_reports/useBidReport";
import { updateBidQuery } from "lib/hooks/api/bid/bids/updateBidCache";
import { useBid } from "lib/hooks/api/bid/bids/useBid";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import {
  useBidOrchestratorStream,
  useBidOrchestratorStreamContext,
} from "lib/providers/bid_orchestrator/BidOrchestratorStreamProvider";
import { routeSegments } from "lib/routes";
import { EventNames, TrackingProvider, useTracking } from "lib/tracking";
import { BidOrchestratorPhase, BidReportType } from "lib/types/graphQLEnums";
import { BID_REPORT_LOADING_COPY } from "../common_report_components/BidReportLoadingPage";
import BidReportLoadingState from "../common_report_components/BidReportLoadingState";
import QualificationQuestions from "./QualificationQuestions";
import { QualificationReportHeader } from "./QualificationReportHeader";
import WinThemes from "./WinThemes";

type Props = {
  bidId: string;
  qualificationReportId: string;
};

export default function QualificationReport({ bidId, qualificationReportId }: Props) {
  const { logEvent } = useTracking();

  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentUser();
  // TODO: Remove winthemes from useBid query and put it on bidReport => bid resolveField instead
  const { data: bid, isLoading: isLoadingBid } = useBid({ id: bidId });
  const { data: bidReport, isLoading: isLoadingBidReport } = useBidReport(qualificationReportId);
  // Live orchestrator state for this bid, if one is streaming in the current session.
  // The streamed answers are folded into the same React Query caches that
  // useBidReport / useGetOrGenerateAllAnswers / useBid read from, so child
  // components don't need direct access to the streaming entry.
  const {
    isQualificationReportStreaming,
    isQualificationSummaryStreaming,
    isWinThemeStreaming,
    hasFailed: enrichmentFailed,
    phase: enrichmentPhase,
  } = useBidOrchestratorStream(bidId);
  const { trackOrchestrator } = useBidOrchestratorStreamContext();

  const { mutate: streamBidReport, isLoading: isDispatching } = useStreamBidReport({
    // Mirror the backend wipe (streamBidReport deletes all bid report entries and win
    // themes up front) so the cached old report can't flash between mutation success
    // and the first streamed chunk. Streaming hydration re-populates as chunks arrive.
    onMutate: () => {
      updateBidReportAnswersCache(queryClient, qualificationReportId, () => ({ result: [] }));
      updateBidQuery(queryClient, bidId, (bid) => ({ ...bid, winThemes: [] }));
    },
    onSuccess: (data) => {
      toast.info("Regenerating qualification report");
      trackOrchestrator(bidId, data.streamBidReport.runId, {
        qualificationReport: true,
        requirementSummary: false,
      });
    },
    onError: () => toast.error("Failed to regenerate qualification report"),
  });

  const onRegenerateBidReport = useCallback(() => {
    logEvent(EventNames.qualificationReportRegenerated, {
      "Action type": "Button clicked",
      "Report type": "Qualification",
    });
    streamBidReport({
      input: {
        bidReportId: qualificationReportId,
        reportType: BidReportType.Qualification,
      },
    });
  }, [qualificationReportId, logEvent, streamBidReport]);

  // Flicker-free handoff: once orchestrator is past the file-processing phase
  // there's already streamed content in the React Query caches, so don't drop
  // back to the loading skeleton even if the persisted bidReport hasn't
  // hydrated yet.
  const hasStreamingContent =
    enrichmentPhase !== BidOrchestratorPhase.Idle &&
    enrichmentPhase !== BidOrchestratorPhase.ProcessingFiles;

  if ((isLoadingBid || isLoadingBidReport || isDispatching) && !hasStreamingContent) {
    const { title, description } = BID_REPORT_LOADING_COPY[routeSegments.qualificationReport];
    return <BidReportLoadingState title={title} description={description} bidId={bidId} />;
  }

  if (!bid) {
    return <Navigate to="/bids/$id" params={{ id: bidId }} />;
  }
  // If the persisted bidReport hasn't arrived yet but we have streaming content,
  // don't redirect — the user is mid-stream or the DB commit hasn't replicated.
  if (!bidReport && !hasStreamingContent) {
    return <Navigate to="/bids/$id" params={{ id: bidId }} />;
  }

  const bidNotice = bid?.notice;
  const bidNoticeBuyer = bidNotice?.buyers?.[0];
  const bidBuyer = bid.buyers?.[0];

  const noticeId = bidNotice?.guid;
  const noticeTitle = bidNotice?.name ?? undefined;
  const bidName = bid.name;

  const hasEditAccess = !!currentUser && currentUser.team.id === bidReport?.teamId;

  return (
    <TrackingProvider data={{ "Report type": "Qualification" }}>
      <QualificationReportPage>
        <QualificationReportHeader
          bidId={bidId}
          bidName={bidName}
          bidReportId={qualificationReportId}
          noticeGuid={noticeId}
          notice={bidNotice}
          urlShareSignature={bidReport?.publicHash ?? undefined}
          hasEditAccess={hasEditAccess}
          winThemes={bid.winThemes}
          onRegenerateBidReport={onRegenerateBidReport}
        />
        {enrichmentFailed && (
          <StreamingErrorBanner role="alert">
            Report creation failed. The content below may be partial. Try regenerating the report.
          </StreamingErrorBanner>
        )}
        <PageContent>
          <Sections>
            <ReportNoticeDetails
              title="Qualification report"
              noticeId={noticeId}
              noticeTitle={noticeTitle}
              buyerName={bidNoticeBuyer?.name ?? bidBuyer?.name}
              buyerId={bidNoticeBuyer?.guid ?? bidBuyer?.id}
              reportId={qualificationReportId}
              bidId={bidId}
              bidName={bidName}
              hasEditAccess={hasEditAccess}
              reportType={BidReportType.Qualification}
            />
            <CollapseGroup>
              <QualificationQuestions
                bidId={bidId}
                bidReportId={qualificationReportId}
                noticeId={noticeId}
                noticeTitle={noticeTitle}
                hasEditAccess={hasEditAccess}
                resourceName={bidName}
                isQualificationReportStreaming={isQualificationReportStreaming}
                isQualificationSummaryStreaming={isQualificationSummaryStreaming}
              />
              <WinThemes
                bidId={bidId}
                bidName={bidName}
                winThemes={bid.winThemes}
                isStreaming={isWinThemeStreaming}
              />
            </CollapseGroup>
          </Sections>
        </PageContent>
      </QualificationReportPage>
    </TrackingProvider>
  );
}

const StreamingErrorBanner = styled.div(({ theme }) => ({
  padding: "12px 32px",
  backgroundColor: theme.colors.sysDestructiveDefault,
  color: theme.colors.sysBackgroundDefault,
  fontSize: 14,
}));

const QualificationReportPage = styled.div({
  flex: 1,
});

const PageContent = styled(DetailsContent)({
  display: "flex",
  paddingTop: 0,
});

const Sections = styled.div({
  display: "flex",
  flexDirection: "column",
  padding: "48px 32px",
  gap: 64,
  width: "100%",
  maxWidth: 1200,
  marginLeft: "auto",
  marginRight: "auto",
});
